/**
 * The post-handoff destination a master-initiated sign-in may ask for.
 *
 * `/from-master` has always landed on `/dashboard`. Master's "Manage on tenant"
 * button needs to land on a specific screen instead, so it sends
 * `next=/settings/team-assignment` on the handoff URL. That value arrives from
 * another origin, so it is treated as untrusted input: only a plain path on
 * THIS origin is honoured, and anything else silently falls back to the
 * caller's default rather than becoming an open redirect out of the tenant.
 *
 * Accepted: a single leading `/` followed by a path (query and hash may ride
 * along) — `/settings/team-assignment`, `/dashboard/reco?tab=open`.
 * Rejected: absolute URLs (`https://evil`), protocol-relative (`//evil`), the
 * backslash forms browsers normalise to those (`/\evil`, `\\evil`), anything
 * not starting with `/`, and any control character (a `next=/x%0A//evil` style
 * smuggle).
 */
export const sameOriginNextPath = (
  raw: string | null | undefined,
): string | null => {
  if (typeof raw !== "string") {
    return null;
  }

  const value = raw.trim();

  if (value === "" || !value.startsWith("/")) {
    return null;
  }

  // `//host` and `/\host` are both read as protocol-relative by browsers.
  if (value.startsWith("//") || value.startsWith("/\\")) {
    return null;
  }

  // eslint-disable-next-line no-control-regex -- rejecting control characters is the point
  if (/[\u0000-\u001f\u007f]/.test(value)) {
    return null;
  }

  return value;
};
